/**
 * DevPlanContext
 *
 * Dev-only plan override — lets you test Pro/League-gated features without
 * touching Firestore. Only active in development (import.meta.env.DEV).
 * The override persists in localStorage so it survives page reloads.
 *
 * Usage:
 *   - Wrap the app with <DevPlanProvider> (already done in main.jsx)
 *   - Read the override with useDevPlanOverride() — returns null in production
 *   - The DevPlanSwitcher component uses useDevPlanSwitcher() to read + write
 */

import { createContext, useContext, useState } from 'react';

const LS_KEY = '__devPlanOverride';
const IS_DEV = import.meta.env.DEV;

const DevPlanContext = createContext(null);

export function DevPlanProvider({ children }) {
  const [override, setOverrideState] = useState(() =>
    IS_DEV ? (localStorage.getItem(LS_KEY) ?? null) : null,
  );

  function setOverride(plan) {
    if (!IS_DEV) return;
    if (plan) {
      localStorage.setItem(LS_KEY, plan);
    } else {
      localStorage.removeItem(LS_KEY);
    }
    setOverrideState(plan ?? null);
  }

  return (
    <DevPlanContext.Provider value={{ override, setOverride }}>
      {children}
    </DevPlanContext.Provider>
  );
}

/**
 * Returns the current dev plan override string ('free' | 'pro' | 'league'),
 * or null if no override is set or the app is running in production.
 */
export function useDevPlanOverride() {
  return useContext(DevPlanContext)?.override ?? null;
}

/**
 * Returns { override, setOverride } — used by DevPlanSwitcher.
 */
export function useDevPlanSwitcher() {
  const ctx = useContext(DevPlanContext);
  return {
    override:    ctx?.override  ?? null,
    setOverride: ctx?.setOverride ?? (() => {}),
  };
}
